// Written by Codex on 2026-10-16
// User prompt: Fix demo connectivity using the dedicated API hostname.
// Written by Claude Code on 2026-01-29
// User prompt: Implement FMLA Deadline & Timeline Tracker Prototype
// Updated by Claude Code on 2026-02-16
// User prompt: Fix chatbot connection error - frontend pointing to localhost instead of deployed backend
// Updated by Claude Code on 2026-02-16
// User prompt: Replace all localhost references with deployed URLs

// Updated by Claude Code on 2026-02-21
// User prompt: now do the backend auth middleware and explain it as you go

import axios from 'axios';
import { auth } from '../firebase';

const DEFAULT_PRODUCTION_API_BASE_URL = 'https://api.certifyai.us/api';

function isLocalFrontend() {
  return ['localhost', '127.0.0.1'].includes(window.location.hostname);
}

function resolveApiBaseUrl() {
  const runtimeApiBaseUrl = window.ENV?.REACT_APP_API_BASE_URL;
  if (runtimeApiBaseUrl) return runtimeApiBaseUrl;

  if (isLocalFrontend()) return '/api';

  return process.env.REACT_APP_API_BASE_URL || DEFAULT_PRODUCTION_API_BASE_URL;
}

function attachLocalDevIdentity(headers, user) {
  if (!isLocalFrontend() || !user) return headers;

  return {
    ...headers,
    'x-dev-user-uid': user.uid,
    'x-dev-user-email': user.email || '',
    'x-dev-user-name': user.displayName || user.email?.split('@')[0] || 'User',
  };
}

const API_BASE_URL = resolveApiBaseUrl();

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Written by Codex on 2026-10-16
// User prompt: Show AI legal monitoring reports in Superadmin.
export const legalMonitoringAPI = {
  report: (options = {}) => api.get('/legal-monitoring', { signal: options.signal, timeout: 15000 }),
};

// This request interceptor runs automatically before every API call.
// It gets the current Firebase user's ID token and attaches it to the
// Authorization header so the backend middleware can verify the request.
//
// getIdToken() is smart — it returns a cached token if it's still valid,
// or silently fetches a fresh one from Firebase if the 1-hour expiry has
// passed. You never need to manage token refresh manually.
api.interceptors.request.use(async (config) => {
  const user = auth.currentUser;
  config.headers = attachLocalDevIdentity(config.headers || {}, user);
  if (user) {
    const token = await user.getIdToken();
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Leave Requests API
// Written by Codex on 2026-10-15
// User prompt: Keep managing the agents and move into integrating these rules into the live workflow. Make sure the changes are well tested and the outstanding legal gaps stay tracked. continue with our implementation
export const leaveRuleContextAPI = {
  get: (id) => api.get(`/leave-rule-context/${id}`),
  update: (id, data) => api.patch(`/leave-rule-context/${id}`, data),
};

// Written by Codex on 2026-10-19
// User prompt: Make employee and HR case messages load and trigger notifications.
export const caseMessagesAPI = {
  list: id => api.get(`/case-messages/${id}/messages`),
  send: (id, data) => api.post(`/case-messages/${id}/messages`, data),
};

export const leaveRequestsAPI = {
  getAll:     (params = {}) => api.get('/leave-requests/', { params }),
  getMine:    (params = {}) => api.get('/leave-requests/mine', { params }),
  getById:    (id) => api.get(`/leave-requests/${id}`),
  create:     (data) => api.post('/leave-requests/', data),
  update:     (id, data) => api.patch(`/leave-requests/${id}`, data),
  // Written by Codex on 2026-10-16
  // User prompt: Complete reviewed phased FMLA schedules.
  updateSchedulePhases: (id, data) => api.patch(`/leave-requests/${id}/schedule-phases`, data),
  delete:     (id) => api.delete(`/leave-requests/${id}`),
  exportCsv:  () => api.get('/leave-requests/export', { responseType: 'blob' }),
  auditPacket:   (id) => api.get(`/leave-requests/${id}/audit-packet`, { responseType: 'blob' }),
  sendSectionI:  (id, sectionIData) => api.post(`/leave-requests/${id}/send-section-i`, { section_i_data: sectionIData }),
  // Intermittent absences
  getAbsences:    (id) => api.get(`/leave-requests/${id}/absences`),
  logAbsence:     (id, data) => api.post(`/leave-requests/${id}/absences`, data),
  deleteAbsence:  (id, absenceId) => api.delete(`/leave-requests/${id}/absences/${absenceId}`),
  // Employer notices (PDF download)
  // Written by Codex on 2026-10-15
  // User prompt: Keep working through the FMLA notices and ADA demo.
  noticeWH381:    (id, options = {}) => api.get(`/leave-requests/${id}/notice/wh-381`, { responseType: 'blob', signal: options.signal, timeout: 20000 }),
  noticeWH382:    (id, options = {}) => api.get(`/leave-requests/${id}/notice/wh-382`, { responseType: 'blob', signal: options.signal, timeout: 20000 }),
  // Return-to-work
  initiateReturn: (id, data = {}) => api.post(`/leave-requests/${id}/initiate-return`, data),
  // Payroll handoff CSV
  payrollReport:  (id) => api.get(`/leave-requests/${id}/payroll-report`, { responseType: 'blob' }),
  // Aggregated data for the HR Reports "Insights" charts.
  getStats:    (params = {}) => api.get('/leave-requests/stats', { params }),
  // Case tasks — internal, HR/manager only. Written by Claude Code on 2026-07-27.
  getTasks:    (id) => api.get(`/leave-requests/${id}/tasks`),
  createTask:  (id, data) => api.post(`/leave-requests/${id}/tasks`, data),
  updateTask:  (id, taskId, data) => api.patch(`/leave-requests/${id}/tasks/${taskId}`, data),
  deleteTask:  (id, taskId) => api.delete(`/leave-requests/${id}/tasks/${taskId}`),
  // Employee-facing subset of the same checklist (taskType 'employee' only) — the
  // "what you need to do" list on the employee My Leaves / Leave Detail pages.
  // Written by Claude Code on 2026-07-29.
  getMyTasks:    (id) => api.get(`/leave-requests/${id}/my-tasks`),
  updateMyTask:  (id, taskId, completed) => api.patch(`/leave-requests/${id}/my-tasks/${taskId}`, { completed }),
  // Case notes — internal, HR/manager only.
  getNotes:    (id) => api.get(`/leave-requests/${id}/notes`),
  createNote:  (id, data) => api.post(`/leave-requests/${id}/notes`, data),
  updateNote:  (id, noteId, data) => api.patch(`/leave-requests/${id}/notes/${noteId}`, data),
};

// Timeline API
export const timelineAPI = {
  getTimeline: (requestId) => api.get(`/timeline/${requestId}`),
  getCompliance: (requestId) => api.get(`/timeline/${requestId}/compliance`),
  getAllAlerts: () => api.get('/timeline/alerts/all'),
};

// Notifications API
export const notificationsAPI = {
  // Written by Codex on 2026-10-19
  // User prompt: Provide an explicit audited SMS delivery test from HR.
  getSmsTestTarget: requestId => api.get('/notifications/sms-test-target', { params: { request_id: requestId } }),
  testSms: (requestId, targetVersion, key) => api.post('/notifications/sms-test', { request_id: requestId, target_version: targetVersion }, { headers: { 'Idempotency-Key': key } }),
  getAll: (params = {}) => api.get('/notifications/', { params }),
  getByRequestId: (requestId) => api.get(`/notifications/${requestId}`),
  create: (params) => api.request({
    url: '/notifications/',
    method: 'post',
    params,
  }),
  update: (id, readStatus) => api.patch(`/notifications/${id}`, { read_status: readStatus }),
  delete: (id) => api.delete(`/notifications/${id}`),
};

// Documents API
// Written by Claude Code on 2026-02-10
// User prompt: Implement document upload & FMLA eligibility tracking
export const documentsAPI = {
  upload: (requestId, file, documentType = 'other', requestLink = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('request_id', requestId);
    formData.append('document_type', documentType);
    // Written by Codex on 2026-10-25; User prompt: Preserve explicit request/replacement context.
    if (requestLink.information_request_id) formData.append('information_request_id', requestLink.information_request_id);
    if (requestLink.supersedes_document_id) formData.append('supersedes_document_id', requestLink.supersedes_document_id);
    return api.post('/documents/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getByRequestId: (requestId) => api.get(`/documents/request/${requestId}`),
  getById: (id) => api.get(`/documents/${id}`),
  // Written by Codex on 2026-10-15
  // User prompt: demo readiness
  download: (id, options = {}) => api.get(`/documents/${id}/download`, { ...options, responseType: 'blob' }),
  getPreviewUrl: (id) => `${API_BASE_URL}/documents/${id}/preview`,
  update: (id, data) => api.patch(`/documents/${id}`, data),
  delete: (id) => api.delete(`/documents/${id}`),
};

// Chatbot API — AI-native HRIS action interface
// Written by Codex on 2026-10-16
// User prompt: Continue Clara confirmation and duplicate-submit protection.
export const chatbotAPI = {
  decideProposal: (conversationId, proposalId, decision) => api.post(`/chat/conversations/${conversationId}/proposals/${proposalId}`, { decision }),
  createConversation: ()                        => api.post('/chat/conversations'),
  listConversations:  ()                        => api.get('/chat/conversations'),
  getMessages:        (conversationId)          => api.get(`/chat/conversations/${conversationId}/messages`),
  sendMessage:        (conversationId, message) => api.post(`/chat/conversations/${conversationId}/messages`, { message }),
  deleteConversation: (conversationId)          => api.delete(`/chat/conversations/${conversationId}`),
  getSuggestions:     ()                        => api.get('/chat/suggestions'),
  // Returns a native fetch Response with an SSE body — use .body.getReader() to stream
  streamMessage: async (conversationId, message) => {
    const user = auth.currentUser;
    const headers = attachLocalDevIdentity({ 'Content-Type': 'application/json' }, user);
    if (user) {
      const token = await user.getIdToken();
      headers.Authorization = `Bearer ${token}`;
    }
    return fetch(`${API_BASE_URL}/chat/conversations/${conversationId}/stream`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ message }),
    });
  },
};

// Workflows API — Natural Language Workflow Definition (Pillar 4)
export const workflowsAPI = {
  getAll:  ()                  => api.get('/workflows'),
  parse:   (description)       => api.post('/workflows/parse', { description }),
  create:  (data)              => api.post('/workflows', data),
  update:  (id, data)          => api.patch(`/workflows/${id}`, data),
  delete:  (id)                => api.delete(`/workflows/${id}`),
  getRuns: (id)                => api.get(`/workflows/${id}/runs`),
};

// Insights API — Proactive Intelligence (Pillar 2)
export const insightsAPI = {
  getAll:  (params = {}) => api.get('/insights', { params }),
  update:  (id, data)    => api.patch(`/insights/${id}`, data),
  runNow:  ()            => api.post('/insights/run'),
};

// Users API (role-based auth)
export const usersAPI = {
  getMe:         ()             => api.get('/users/me'),
  updateMe: (data) => api.patch('/users/me', data),
  uploadAvatar: (avatarDataUrl) => api.patch('/users/me', { avatar_url: avatarDataUrl }),
  updateBankStatus: (userId, status) => api.patch(`/users/${userId}/bank-status`, { status }),
  getAll: () => api.get('/users/'),
  inviteAsHr: (payload) => api.post('/users/hr/invite', payload),
  inviteAsSuperAdmin: (payload) => api.post('/users/super-admin/invite', payload),
  updateRole: (userId, role) => api.patch(`/users/${userId}/role`, { role }),
  updateSettingsAccess: (userId, canManageSettings) => api.patch(`/users/${userId}/settings-access`, { can_manage_settings: canManageSettings }),
  updateProfile:      (userId, data) => api.patch(`/users/${userId}/profile`, data),
  updateLeaveBalance: (userId, data) => api.patch(`/users/${userId}/leave-balance`, data),
  deleteAsHr: (userId) => api.delete(`/users/hr/${userId}`),
  deleteAsSuperAdmin: (userId) => api.delete(`/users/super-admin/${userId}`),
  // HR: view/download an employee's signed onboarding documents
  getEmployeeDocuments:    (userId)        => api.get(`/users/${userId}/documents`),
  downloadEmployeeDocument:(userId, docId) => api.get(`/users/${userId}/documents/${docId}/download`, { responseType: 'blob' }),
  // SSN (full, decrypted — only for authenticated user themselves)
  updateEmergencyContacts: (contacts) => api.patch('/users/me', { emergency_contacts: contacts }),
  getMySSN:           ()             => api.get('/users/me/ssn'),
  // Onboarding signed forms
  getMyDocuments:     ()             => api.get('/users/me/documents'),
  signI9:             (data)         => api.post('/users/me/forms/i9', data),
  signOfferLetter:    (data)         => api.post('/users/me/forms/offer-letter', data),
  downloadSignedDoc:  (docId)        => api.get(`/users/me/forms/${docId}/download`, { responseType: 'blob' }),
};

// Onboarding API
export const onboardingAPI = {
  getAll:       ()                           => api.get('/onboarding'),
  getMe:        ()                           => api.get('/onboarding/me'),
  markStep:     (userId, step, completed)    => api.patch(`/onboarding/${userId}/step`, { step, completed }),
  markMyStep:   (step, completed)            => api.patch('/onboarding/me/step', { step, completed }),
};

export const clientsAPI = {
  getAll: () => api.get('/clients/'),
  create: (payload) => api.post('/clients/', payload),
  delete: (id) => api.delete(`/clients/${id}`),
  // Module management (super-admin)
  getModules:    (id)           => api.get(`/clients/${id}/modules`),
  setModules:    (id, modules)  => api.patch(`/clients/${id}/modules`, { available_modules: modules }),
  // Offer letter
  getOfferLetterInfo:   ()     => api.get('/clients/me/offer-letter/info'),
  getOfferLetter:       ()     => api.get('/clients/me/offer-letter', { responseType: 'blob' }),
  uploadOfferLetter:    (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/clients/me/offer-letter', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteOfferLetter:    ()     => api.delete('/clients/me/offer-letter'),
};

// Process API — AI-powered FMLA form analysis (SSE streaming)
// Returns a fetch Response with a text/event-stream body.
// Use response.body.getReader() to consume the stream.
// Pass documentId so the backend can persist AI results to the Document record.
export const processAPI = {
  processDocument: async (file, documentId = null, { signal, reanalyze = false } = {}) => {
    const user = auth.currentUser;
    const headers = attachLocalDevIdentity({}, user);
    if (user) {
      const token = await user.getIdToken();
      headers.Authorization = `Bearer ${token}`;
    }
    const form = new FormData();
    form.append('file', file);
    if (documentId) form.append('documentId', documentId);
    if (reanalyze) form.append('reanalyze', 'true');
    return fetch(`${API_BASE_URL}/process`, {
      method: 'POST',
      headers,
      body: form,
      signal,
    });
  },
};

// Extraction Results API — stored AI analysis per document
export const extractionResultsAPI = {
  get: (documentId, options = {}) => api.get(`/extraction-results/${documentId}`, options),
  update: (documentId, corrections) => api.patch(`/extraction-results/${documentId}`, corrections),
};

// Audit Log API — immutable event trail
export const auditLogAPI = {
  getByEntity: (entityId, entityType) =>
    api.get('/audit-log', { params: { entityId, entityType } }),
};

// State Laws API — applicable state leave law overlay per request
export const stateLawsAPI = {
  getStateLaws: (requestId) => api.get(`/leave-requests/${requestId}/state-laws`),
};

// Leave Planning API — private, employee-only "what if" scenario tool.
// Written by Claude Code on 2026-07-23
// User prompt: Let's implement that. Do a more specific research into what exactly that feature
// involves in aidora, and then work on implementing that with the same ui/ux and styling that we
// use throughout the app
export const leavePlanningAPI = {
  getContext: () => api.get('/leave-planning/context'),
  getMine: () => api.get('/leave-planning/mine'),
  save: (data) => api.put('/leave-planning/mine', data),
  clear: () => api.delete('/leave-planning/mine'),
  evaluate: (data) => api.post('/leave-planning/evaluate', data),
  chat: (data) => api.post('/leave-planning/chat', data),
};

// Leave Policy versioning — HR-only. Versions are immutable once created; "editing"
// means creating a new version (see backend-node/src/routes/leavePolicies.js).
// Written by Claude Code on 2026-07-27
export const leavePoliciesAPI = {
  list: (leaveType) => api.get('/leave-policies', { params: leaveType ? { leave_type: leaveType } : {} }),
  getById: (id) => api.get(`/leave-policies/${id}`),
  createVersion: (data) => api.post('/leave-policies', data),
};

// Workplace Locations — HR settings. Written by Claude Code on 2026-07-27
export const workplaceLocationsAPI = {
  list: () => api.get('/workplace-locations'),
  create: (data) => api.post('/workplace-locations', data),
  update: (id, data) => api.patch(`/workplace-locations/${id}`, data),
  delete: (id) => api.delete(`/workplace-locations/${id}`),
};

// Pay estimation — week-by-week leave pay (state PFML + employer top-up)
// Written by Claude Code on 2026-07-22
export const payEstimateAPI = {
  // params: { weekly_wage?, annual_salary?, state?, weeks? }
  forRequest: (requestId, params = {}) => api.get(`/leave-requests/${requestId}/pay-estimate`, { params }),
  // body: { weekly_wage|annual_salary|hourly_rate, state, reason|condition_type, start_date, end_date|duration_weeks, policy? }
  preview: (body) => api.post('/pay-estimate/preview', body),
  // body: { company:{ headcount, avg_annual_salary, hr_annual_salary?, hr_hourly_cost?, certify_annual_cost }, leaves:[{state,type,weeks}], policy?, assumptions? }
  roi: (body) => api.post('/pay-estimate/roi', body),
  // body: { leave_request_ids:[...], start_date?, end_date?, scheduled_hours_per_week?, salary_rates?, std_policy_id? }
  payrollReconciliation: (body) => api.post('/pay-estimate/payroll-reconciliation', body),
  exportPayrollReconciliation: (body) => api.post('/pay-estimate/payroll-reconciliation/export', body, { responseType: 'blob' }),
};

// Benefit recovery loop — estimated vs actually-recovered state/STD benefit per leave.
// Written by Claude Code on 2026-07-24.
export const benefitRecoveryAPI = {
  forLeave: (leaveRequestId) => api.get(`/benefit-recovery/leave/${leaveRequestId}`),
  // patch: { status?, filed_at?, recovered_amount?, decided_at?, notes? }
  update: (claimId, patch) => api.patch(`/benefit-recovery/${claimId}`, patch),
  summary: () => api.get('/benefit-recovery/summary'),
};

// Benefit-premium collection during unpaid leave weeks.
// Written by Claude Code on 2026-07-24.
export const premiumCollectionAPI = {
  list: (status) => api.get('/premium-collection', { params: status ? { status } : {} }),
  forLeave: (leaveRequestId) => api.get(`/premium-collection/leave/${leaveRequestId}`),
  // patch: { status?, collected_amount?, method?, notes? }
  update: (id, patch) => api.patch(`/premium-collection/${id}`, patch),
  summary: () => api.get('/premium-collection/summary'),
};

// Org-wide benefit-leakage rollup — both levers in one picture.
// Written by Claude Code on 2026-07-28.
export const leakageAPI = {
  summary: () => api.get('/leakage/summary'),
  // Seeds estimates for leaves never estimated, so the summary covers the whole org.
  scan: () => api.post('/leakage/scan'),
};

// Calendar / Timeline download
export const calendarAPI = {
  getIcs: (requestId) =>
    api.get(`/timeline/${requestId}/calendar.ics`, { responseType: 'blob' }),
  getGoogleCalendarUrl: (requestId) =>
    `${API_BASE_URL}/timeline/${requestId}/calendar.ics`,
  // Per-event "Add to Google Calendar" links (no OAuth) for a leave request's key dates.
  getLinks: (requestId) => api.get(`/timeline/${requestId}/calendar-links`),
};

// Integrations API — HRIS connections and CSV import.
// Every HRIS provider (ADP, Rippling, BambooHR, Workday, and the registry providers) now
// flows through the one generic /integrations/hris/:provider path below.
export const integrationsAPI = {
  // Google Drive (document sync — real Google OAuth + Picker + Drive API)
  getGoogleDriveStatus: ()               => api.get('/integrations/google-drive'),
  prepareGoogleDriveOAuth: ()            => api.post('/integrations/google-drive/oauth/prepare'),
  getGoogleDrivePickerToken: ()          => api.get('/integrations/google-drive/picker-token'),
  saveGoogleDriveFolder: (folderId, folderName) => api.post('/integrations/google-drive/folder', { folder_id: folderId, folder_name: folderName }),
  disconnectGoogleDrive: ()              => api.delete('/integrations/google-drive'),
  syncGoogleDrive:      ()               => api.post('/integrations/google-drive/sync'),
  getGoogleDriveFiles:  (page, pageSize) => api.get('/integrations/google-drive/files', { params: { page, page_size: pageSize } }),
  // Generic HRIS catalog (registry framework — Gusto, Paylocity, UKG Pro, TriNet, ...)
  getHrisCatalog:       ()                  => api.get('/integrations/hris'),
  saveHrisCredentials:  (provider, payload) => api.post(`/integrations/hris/${provider}`, payload),
  disconnectHris:       (provider)          => api.delete(`/integrations/hris/${provider}`),
  // Written by Codex on 2026-10-21
  // User prompt: Read-only Oracle preview without saving a connection.
  previewHrisEmployees: (provider, payload) => payload ? api.post(`/integrations/hris/${provider}/preview`, payload) : api.get(`/integrations/hris/${provider}/preview`),
  syncHrisEmployees:    (provider)          => api.post(`/integrations/hris/${provider}/sync`),
  setHrisWriteback:     (provider, enabled) => api.patch(`/integrations/hris/${provider}/writeback`, { enabled }),
  syncHrisLeaves:       (provider)          => api.post(`/integrations/hris/${provider}/leave-sync`),
  getHrisSyncStatus:    ()                  => api.get('/integrations/hris/sync-status'),
  // CSV Import
  csvImport: (formData) => api.post('/integrations/csv/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  // Org settings
  updateOrg:       (payload) => api.patch('/integrations/org', payload),
  getSettings:     ()        => api.get('/integrations/settings'),
  updateSettings:  (payload) => api.patch('/integrations/settings', payload),
};

// Accommodations API — ADA/PWFA accommodation case management
// Written by Codex on 2026-10-15
// User prompt: lets keep going with the ada workflow. have the agents work on employee-facing decisions and follow-up reminders, keep everything well tested, and track the remaining legal gaps
export const accommodationsAPI = {
  // Written by Codex on 2026-10-19
  // User prompt: Include accommodation to-dos in the HR dashboard attention list.
  getAttentionSummary: () => api.get('/accommodations/attention-summary'),
  // Written by Codex on 2026-10-15
  // User prompt: continue
  getDeliveryRecords: (id) => api.get(`/accommodations/${id}/delivery-records`),
  recordDelivery: (id, data) => api.post(`/accommodations/${id}/delivery-records`, data),
  // Written by Codex on 2026-10-15
  // User prompt: Go
  replyToResponse: (id, responseId, data) => api.post(`/accommodations/${id}/responses/${responseId}/replies`, data),
  handleResponse: (id, responseId, data) => api.post(`/accommodations/${id}/responses/${responseId}/handling`, data),
  getManagement: (id) => api.get(`/accommodations/${id}/management`),
  updateManagement: (id, data) => api.patch(`/accommodations/${id}/management`, data),
  // Written by Codex on 2026-10-15
  // User prompt: lets keep going with the ada workflow. have the agents work on employee responses and acknowledgments next, keep everything well tested, and continue closing the legal gaps.
  getResponses: (id) => api.get(`/accommodations/${id}/responses`),
  respondToDecision: (id, data) => api.post(`/accommodations/${id}/responses`, data),
  reviewResponse: (id, responseId, data) => api.patch(`/accommodations/${id}/responses/${responseId}`, data),
  getDecisions: (id) => api.get(`/accommodations/${id}/decisions`),
  publishDecision: (id, data) => api.post(`/accommodations/${id}/decisions`, data),
  getAll:  ()         => api.get('/accommodations/'),
  getById: (id)       => api.get(`/accommodations/${id}`),
  create:  (data)     => api.post('/accommodations/', data),
  update:  (id, data) => api.patch(`/accommodations/${id}`, data),
};

// Incident Reports API — HR/manager incident tracking
export const incidentReportsAPI = {
  getAll:  (params = {}) => api.get('/incident-reports', { params }),
  getById: (id)          => api.get(`/incident-reports/${id}`),
  create:  (data)        => api.post('/incident-reports', data),
  update:  (id, data)    => api.patch(`/incident-reports/${id}`, data),
  delete:  (id)          => api.delete(`/incident-reports/${id}`),
};

export const famliAPI = {
  getCredentials:  ()           => api.get('/famli/credentials'),
  saveCredentials: (data)       => api.post('/famli/credentials', data),
  deleteCredentials: ()         => api.delete('/famli/credentials'),
  listReports:     ()           => api.get('/famli/reports'),
  createReport:    (data)       => api.post('/famli/reports', data),
  getReport:       (id)         => api.get(`/famli/reports/${id}`),
  deleteReport:    (id)         => api.delete(`/famli/reports/${id}`),
  upsertEntry:     (id, data)   => api.put(`/famli/reports/${id}/entries`, data),
  deleteEntry:     (id, userId) => api.delete(`/famli/reports/${id}/entries/${userId}`),
  autofillEntries: (id, data = {}) => api.post(`/famli/reports/${id}/autofill`, data),
  preview:         (id)         => api.get(`/famli/reports/${id}/preview`),
  submit:          (id)         => api.post(`/famli/reports/${id}/submit`),
};

// Doctor Form API — public endpoints, no auth token needed
const publicApiBase = API_BASE_URL;
export const doctorFormAPI = {
  getContext: (token) => fetch(`${publicApiBase}/doctor-form/${token}`).then((r) => r.json()),
  submit:     (token, data) => fetch(`${publicApiBase}/doctor-form/${token}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  }).then((r) => r.json()),
};

export default api;
